import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Typography,
  Divider,
} from '@material-ui/core'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useRedirect, useTranslate } from 'react-admin'
import { USER_ADDONS } from '../../../utils/constants/userAddons'
import ORGANISATION_TYPES from '../../../utils/constants/OrganisationTypes'
import UseQueryParams from '../../../hooks/useQueryParams'
import youtubeUnlink from '../../../Apis/youtube/youtubeUnlink'
import AppIntegration from '../AppIntegration/AppIntegration'
import youtubeAuthorizeScreen from '../../../Apis/youtube/youtubeAuthorizeScreen'
import QueryKeys from '../../../utils/constants/queryKeys'
import googleAuthorizationCallback from '../../../Apis/youtube/googleAuthorizationCallback'
import localStorageIds from '../../../utils/constants/localStorageIds'
import CardWrapperMoreIcon from '../../../components/layout/CardWrapperMoreIcon/CardWrapperMoreIcon'
import appsPageStyles from './appsPage.styles'
import YoutubeConfigurationForm from '../YoutubeConfigurationForm/YoutubeConfigurationForm'
import youtubeLogoImage from '../../../assets/images/youtubeLogoImage.png'
import FacebookLogo from '../../../assets/images/FacebookLogo.png'
import FacebookLogoInvertedColor from '../../../assets/images/FacebookLogoInvertedColor.png'
import GoogleSignInIcon from '../../../assets/icons/GoogleSignInIcon'
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props'
import getAccounts from '../../../Apis/social/getAccounts'
import getLongLivedToken from '../../../Apis/social/getLongLivedToken'
import postUnlinkSocials from '../../../Apis/social/unlinkSocials'
import getAccountStatus from '../../../Apis/social/getAccountStatus'
import isUserAddonsValid from '../../../utils/helpers/isUserAddonsValid'
import isOrgAddonsValid from '../../../utils/helpers/isOrgAddonsValid'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import MetaAccountChoice from '../../Distribution/MetaAccountChoice/MetaAccountChoice'
import { FB_APP_ID, FB_SCOPE } from '../../../utils/constants/facebookLogin'
import ApiKeyComponent from '../../../components/ApiKeys/ApiKeyComponent'
import MobileApps from '../../../components/MobileApps/MobileApps'
import WidgetsSection from '../WidgetsSection/WidgetsSection'
import { youtubeNeedsReauthorization } from '../../../utils/helpers/youtubeLink'

// Integrations page (formerly "Apps"). Clubs (and other non-federation orgs) connect their
// single org-level Meta account here, like before — minus the old server-rendered config form,
// which the Engage refactor retired. Federations connect per competition on the Distribution
// page instead (BUS-2799), so the Meta card is hidden for them.
const AppsPage = () => {
  const [metaAccounts, setMetaAccounts] = useState([])
  const [facebookLoginInitiated, setFacebookLoginInitiated] = useState(false)
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_ORG]: selectedOrg } = localStorageValue || {}
  const redirectToExternalUrl = useRedirect()
  const queryParams = UseQueryParams()
  const queryClient = useQueryClient()
  const { code, org: orgId } = queryParams
  const translate = useTranslate()

  const isFederation = selectedOrg?.type === ORGANISATION_TYPES.ORG_FEDERATION

  const googleOAuthParams = useRef({ code, orgId })
  if (code) {
    googleOAuthParams.current = { code, orgId }
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('scope')
    url.searchParams.delete('iss')
    window.history.replaceState({}, '', url.toString())
  }
  const useStyles = makeStyles(appsPageStyles)
  const classes = useStyles()

  const youTubeChannelName = selectedOrg?.youTubeChannelName || ''
  // Google refused the stored refresh token: the channel is still on file but every broadcast
  // call fails, so the card has to read as broken rather than as connected.
  const youTubeNeedsReconnect = youtubeNeedsReauthorization(selectedOrg)
  const hasEngage = Boolean(selectedOrg?.addons?.includes(USER_ADDONS.ENGAGE))

  // The org payload no longer carries a social flag (dropped with BUS-2798), so ask the Social
  // slice directly — the same status endpoint the federation Social tab uses, at org level.
  const socialStatusKey = [QueryKeys.GET_SOCIAL_ACCOUNT_STATUS, orgId, null]
  const socialStatus = useQuery(socialStatusKey, () => getAccountStatus({ orgId }), {
    enabled: Boolean(orgId) && hasEngage && !isFederation,
    retry: false,
  })
  const isSocialConnected = Boolean(socialStatus.data?.connected)
  const socialPlatforms = [
    socialStatus.data?.facebookConnected && 'Facebook',
    socialStatus.data?.instagramConnected && 'Instagram',
  ]
    .filter(Boolean)
    .join(' · ')
  const invalidateSocialStatus = () => queryClient.invalidateQueries(socialStatusKey)

  const invalidateQueries = () => {
    queryClient.invalidateQueries(QueryKeys.GET_ORGS_AFTER_LOGIN).then(() => {
      queryClient.invalidateQueries(QueryKeys.GET_TEAMS_AFTER_LOGIN).then(() => {})
    })
  }

  const isInvalidating = () => {
    return (
      queryClient.isFetching(QueryKeys.GET_ORGS) +
        queryClient.isFetching(QueryKeys.GET_ORGS_AFTER_LOGIN) +
        queryClient.isFetching(QueryKeys.GET_TEAMS_AFTER_LOGIN) >
      0
    )
  }

  // YouTube related mutations and queries
  const YoutubeAuthorizeScreen = useMutation(
    // The consent screen is the same for every org; on a cold load the URL may not carry the org yet.
    () => youtubeAuthorizeScreen({ orgId: orgId || selectedOrg?.id }),
    {
      onSuccess: (res) => {
        redirectToExternalUrl(res.data)
      },
    },
  )

  const youtubeUnlinkMutation = useMutation(() => youtubeUnlink({ orgId }), {
    onSuccess: () => {
      invalidateQueries()
    },
  })

  const googleCallback = useQuery(
    [QueryKeys.GOOGLE_AUTHORIZATION_CALLBACK],
    () =>
      googleAuthorizationCallback({
        code: googleOAuthParams.current.code,
        orgId: googleOAuthParams.current.orgId,
      }),
    {
      enabled: Boolean(code && orgId),
      retry: false,
      onSuccess: () => {
        invalidateQueries()
      },
      onError: () => {},
    },
  )

  // Meta related mutations (org-level connect; the page token never lands in the URL — BUS-2837)
  const metaAccountsMutation = useMutation((data) => getAccounts(data), {
    onSuccess: (res) => {
      setMetaAccounts(res)
    },
  })

  const metaLongLivedToken = useMutation((accessToken) => getLongLivedToken({ orgId, accessToken }), {
    onSuccess: (res) => {
      metaAccountsMutation.mutate(res)
    },
  })

  const unlinkSocialsMutation = useMutation(() => postUnlinkSocials({ orgId }), {
    onSuccess: () => {
      invalidateSocialStatus()
    },
  })

  // Only process a Facebook response we actually triggered.
  const metaCallback = useCallback(
    (res) => {
      if (!facebookLoginInitiated) return
      setFacebookLoginInitiated(false)
      if (!res?.accessToken) return
      metaLongLivedToken.mutate(res.accessToken)
    },
    [facebookLoginInitiated, metaLongLivedToken],
  )

  const handleFacebookClick = useCallback((onClick) => {
    setFacebookLoginInitiated(true)
    onClick()
  }, [])

  const FacebookLoginButton = (
    <FacebookLogin
      appId={FB_APP_ID}
      autoLoad={false}
      scope={FB_SCOPE}
      fields='name,email,picture'
      size='small'
      callback={metaCallback}
      isDisabled={
        !isUserAddonsValid([USER_ADDONS.ENGAGE]) && !isOrgAddonsValid([USER_ADDONS.ENGAGE])
      }
      render={({ onClick, isDisabled, isProcessing }) => (
        <button
          onClick={() => handleFacebookClick(onClick)}
          disabled={isDisabled || isProcessing}
          className={classes.metaButton}
        >
          <img style={{ width: 25, height: 25 }} src={FacebookLogoInvertedColor} alt='img' />
          {translate('ra.text.continueWithFB')}
        </button>
      )}
    />
  )

  // Check if the user has specific addons
  const hasAddon = (addonName) => {
    return selectedOrg?.addons?.includes(addonName)
  }

  // Define integrations configuration. The Meta card is org-level and only for non-federation
  // orgs; federations manage social per competition on the Distribution page.
  const integrations = useMemo(
    () => [
      {
        id: 'youtube',
        addon: USER_ADDONS.LIVESTREAM,
        isEnabled: hasAddon(USER_ADDONS.LIVESTREAM),
        linkMutation: hasAddon(USER_ADDONS.LIVESTREAM)
          ? YoutubeAuthorizeScreen
          : { mutate: () => {} },
        unlinkMutation: hasAddon(USER_ADDONS.LIVESTREAM)
          ? youtubeUnlinkMutation
          : { mutate: () => {} },
        icon: <img className={classes.youtubeImage} src={youtubeLogoImage} alt='YouTube logo' />,
        text: translate('ra.text.youtubeIntegrationText'),
        infoText: (
          <div dangerouslySetInnerHTML={{ __html: translate('ra.text.youtubeIntegrationInfo') }} />
        ),
        linkButton: <GoogleSignInIcon />,
        isLoading:
          youtubeUnlinkMutation.isLoading ||
          googleCallback.isLoading ||
          YoutubeAuthorizeScreen.isLoading ||
          isInvalidating(),
        linkedAccountName: hasAddon(USER_ADDONS.LIVESTREAM) ? youTubeChannelName : '',
        warningText:
          hasAddon(USER_ADDONS.LIVESTREAM) && youTubeNeedsReconnect
            ? translate('ra.text.youtubeReconnectRequired')
            : null,
        configurationForm: <YoutubeConfigurationForm />,
      },
      ...(!isFederation
        ? [
            {
              id: 'meta',
              addon: USER_ADDONS.ENGAGE,
              isEnabled: hasAddon(USER_ADDONS.ENGAGE),
              linkMutation: { mutate: () => {} },
              unlinkMutation: hasAddon(USER_ADDONS.ENGAGE)
                ? unlinkSocialsMutation
                : { mutate: () => {} },
              icon: <img className={classes.fbImage} src={FacebookLogo} alt='Facebook logo' />,
              text: translate('ra.text.metaIntegrationText'),
              linkButton: FacebookLoginButton,
              isLoading:
                metaLongLivedToken.isLoading ||
                metaAccountsMutation.isLoading ||
                unlinkSocialsMutation.isLoading ||
                socialStatus.isLoading,
              linkedAccountName:
                hasAddon(USER_ADDONS.ENGAGE) && isSocialConnected
                  ? translate('ra.text.metaConnected', { platforms: socialPlatforms || 'Meta' })
                  : '',
              // No configuration form — the server-side Meta config/posting was retired.
              configurationForm: null,
            },
          ]
        : []),
    ],
    [
      classes,
      translate,
      youTubeChannelName,
      youTubeNeedsReconnect,
      isSocialConnected,
      socialPlatforms,
      socialStatus.isLoading,
      isFederation,
      YoutubeAuthorizeScreen,
      youtubeUnlinkMutation,
      googleCallback,
      isInvalidating,
      metaLongLivedToken,
      metaAccountsMutation,
      unlinkSocialsMutation,
      selectedOrg,
      FacebookLoginButton,
    ],
  )

  return (
    <CardWrapperMoreIcon>
      <Card className={classes.card}>
        <CardHeader
          title={
            <Typography className={classes.cardTitle} variant='h5'>
              {translate('ra.text.integrations')}
            </Typography>
          }
          subheader={
            <Typography className={classes.cardSubtitle}>
              {translate('ra.text.manageIntegrations')}
            </Typography>
          }
        />
        <CardContent>
          {integrations.map((integration) => (
            <div key={integration.id}>
              <AppIntegration
                linkMutation={integration.linkMutation}
                unLinkMutation={integration.unlinkMutation}
                icon={integration.icon}
                text={integration.text}
                infoText={integration.isEnabled ? integration.infoText : null}
                linkButton={integration.linkButton}
                isLoading={integration.isLoading}
                linkedAccountName={integration.linkedAccountName}
                configurationForm={integration.isEnabled ? integration.configurationForm : null}
                disabled={!integration.isEnabled}
                disabledMessage={
                  !integration.isEnabled
                    ? translate('ra.text.integrationRequiresAddon', {
                        addon: integration.addon,
                      })
                    : ''
                }
                warningText={integration.isEnabled ? integration.warningText : null}
              />
            </div>
          ))}

          <ApiKeyComponent />

          <Divider variant='middle' className={classes.divider} />

          <MobileApps />

          <Divider variant='middle' className={classes.divider} />

          <WidgetsSection />

          {!isFederation && (
            <MetaAccountChoice
              accounts={metaAccounts}
              setMetaAccounts={setMetaAccounts}
              orgId={orgId}
              competitionId={null}
              onConnected={invalidateSocialStatus}
            />
          )}
        </CardContent>
      </Card>
    </CardWrapperMoreIcon>
  )
}

export default AppsPage
